import { useRef } from "react";

import { FlatList, View } from "react-native";
import { useTheme, Text } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

type Props<T> = {
  values: T[];
  selectedValue: T;
  selectValue: (value: T) => void;
  padEdges?: boolean;
};

export default function TabBar<T>({
  values,
  selectedValue,
  selectValue,
  padEdges,
}: Props<T>) {
  const ref = useRef<FlatList>(null);

  const select = (value: T, index: number) => () => {
    selectValue(value);
    ref.current?.scrollToIndex({ index, animated: true });
  };

  const tailwind = useTailwind();

  return (
    <FlatList
      ref={ref}
      data={values}
      horizontal
      keyExtractor={(item) => item}
      bounces={false}
      renderItem={({ item, index }) => (
        <Tab
          value={item}
          selected={item === selectedValue}
          onPress={select(item, index)}
        />
      )}
      contentContainerStyle={tailwind(`${padEdges ? "mx-2" : ""} gap-4`)}
      showsHorizontalScrollIndicator={false}
      style={tailwind("flex-grow-0")}
    />
  );
}

type TabProps = {
  value: string;
  selected: boolean;
  disabled?: boolean;
  onPress: () => void;
};
function Tab({ value, selected, disabled = false, onPress }: TabProps) {
  const theme = useTheme();
  const tailwind = useTailwind();

  const selectedStyle = selected && {
    height: 3,
    backgroundColor: theme.colors.secondary,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
  };

  const disabledStyle = disabled && {
    color: theme.colors.onSurfaceDisabled,
  };

  return (
    <View>
      <Text
        variant="labelMedium"
        style={[tailwind("font-semibold py-3"), disabledStyle]}
        onPress={onPress}
      >
        {value}
      </Text>
      <View style={selectedStyle} />
    </View>
  );
}
